import { lastStopPassed, nextStopAhead, stopsRemaining } from "./progress";
import { ARRIVING_SECONDS } from "./ride";
import type { CardQuestion } from "./rider-journey";
import { formatClock as formatZonedClock, zonedParts } from "../shared/time";
import { headsignLabel, stationLabel } from "../shared/labels";
import type { CommutePlan, CommuteSnapshot, PlanStatus, ServiceHealth, Urgency } from "../shared/types";

export interface ClockParts {
	time: string;
	meridiem: string;
}

/** Split form, so the meridiem can be set smaller beside a large number. */
export function clockParts(epochMs: number | null | undefined, timezone: string): ClockParts {
	if (epochMs == null) {
		return { time: "--:--", meridiem: "" };
	}
	return { time: formatZonedClock(epochMs, timezone), meridiem: zonedParts(epochMs, timezone).hour < 12 ? "am" : "pm" };
}

export function clock(epochMs: number | null | undefined, timezone: string): string {
	const parts = clockParts(epochMs, timezone);
	return parts.meridiem ? `${parts.time} ${parts.meridiem}` : parts.time;
}

export function minutes(seconds: number | null | undefined): string {
	if (seconds == null) {
		return "--";
	}
	return `${Math.round(seconds / 60)}`;
}

export function signedMinutes(seconds: number | null | undefined): string {
	if (seconds == null) {
		return "";
	}
	const value = Math.round(seconds / 60);
	if (value === 0) {
		return "on time";
	}
	return value > 0 ? `+${value}` : `${value}`;
}

export function relativeAge(epochMs: number | null, nowMs: number): string {
	if (epochMs == null) {
		return "never";
	}
	return `${duration(Math.max(0, Math.round((nowMs - epochMs) / 1000)))} ago`;
}

/** Compact age, so a feed that is days behind never reads as a big number of seconds. */
export function duration(seconds: number): string {
	if (seconds < 60) {
		return `${seconds}s`;
	}
	if (seconds < 3600) {
		return `${Math.round(seconds / 60)}m`;
	}
	if (seconds < 86_400) {
		return `${Math.round(seconds / 3600)}h`;
	}
	return `${Math.round(seconds / 86_400)}d`;
}

export type Tone = "good" | "warn" | "bad" | "info" | "neutral";

export interface CardStatus {
	/** Small word above the hero number, e.g. "LEAVE BY". Null when the hero is itself a verb. */
	lead: string | null;
	hero: string;
	/** Set when the hero is a clock time, so the meridiem can be styled separately. */
	heroMeridiem: string | null;
	unit: string | null;
	sub: string;
	/** `sub` split so the clock times can be picked out. Falls back to `sub` when absent. */
	subParts?: Array<{ text: string; time?: boolean }>;
	/**
	 * A second line under `sub`: the margin that decides whether he makes it, and how it is doing.
	 *
	 * The label carries the tense. A margin he has not earned yet is "to spare at departure"; the
	 * live one is "to spare". Reading the two the same way is what made "47 minutes to spare" appear
	 * beside "LEAVE IN 40 minutes".
	 */
	note?: { value: string; label: string; tone: Tone };
	tone: Tone;
}

/**
 * The one line the app exists to produce. Everything else on the card explains it.
 *
 * Outbound answers "when do I walk out of the door". Return mode has no travel leg, so the
 * same card answers "how long until this train leaves" instead of inventing a departure time.
 */
/** Beyond this the plan is about another part of the day, so countdowns stop being useful. */
const FAR_FUTURE_MINUTES = 240;

/**
 * Which question the card is answering.
 *
 * `leave` counts down to the deadline. `drive` compares drop-off with the train, because the
 * deadline is behind him. `platform` counts the train down, because he is standing there.
 * `onboard` stops asking about catching it, because he is on it.
 */
export type CardMode = CardQuestion;

/** Metres per mile, because the card is read by someone who thinks in miles. */
const METERS_PER_MILE = 1609.344;

/**
 * The last leg, which no train is involved in.
 *
 * Outbound that is the shuttle up to College Prep; on the way home it is the drive to whichever
 * house he is going to. The distance is straight-line and exact; the minutes are the same
 * modelled figure the plan used for that leg all morning, so they are quoted as an estimate.
 */
export interface OnwardLeg {
	/** Which place this is, for anything that has to address it rather than print it. */
	id: string;
	label: string;
	place: { lat: number; lon: number };
	/** Straight line from where the phone is. Null with no fix. */
	metersAway: number | null;
	seconds: number | null;
	/** Road distance, once the route has come back. Beats the straight line when present. */
	roadMeters?: number | null;
	/** True while the minutes are the modelled figure rather than a routed one. */
	estimated?: boolean;
}

export function onwardStatus(leg: OnwardLeg | null, homeward: boolean, saying: string | null): CardStatus {
	const greeting = homeward ? "Welcome home!" : "Have a great day!";
	if (!leg) {
		return { lead: null, hero: greeting, heroMeridiem: null, unit: null, sub: saying ?? "", tone: "good" };
	}
	const meters = leg.roadMeters ?? leg.metersAway;
	if (meters == null) {
		return { lead: leg.label.toUpperCase(), hero: "--", heroMeridiem: null, unit: null, sub: "No fix yet.", tone: "info" };
	}
	const miles = meters / METERS_PER_MILE;
	const minutes = leg.seconds == null ? null : Math.round(leg.seconds / 60);
	return {
		lead: leg.label.toUpperCase(),
		hero: miles < 10 ? miles.toFixed(1) : `${Math.round(miles)}`,
		heroMeridiem: null,
		unit: miles >= 0.95 && miles < 1.05 ? "mile" : "miles",
		sub: minutes == null ? "away" : leg.estimated ? `about ${minutes} min away` : `${minutes} min in traffic`,
		tone: "good",
	};
}

export function cardStatus(
	plan: CommutePlan | null,
	hasTravelLeg: boolean,
	nowMs: number,
	timezone: string,
	mode: CardMode = "leave",
	/** Which stop the onboard card counts down to. The ride home has two and he picks. */
	destinationIndex = 0,
): CardStatus {
	const verb = (hero: string, sub: string, tone: Tone, note?: CardStatus["note"], subParts?: CardStatus["subParts"]): CardStatus => ({
		lead: null,
		hero,
		heroMeridiem: null,
		unit: null,
		sub,
		subParts,
		note: temper(note, tone),
		tone,
	});
	// The clock time is the part worth reading, on these cards as much as on the countdown.
	const says = (before: string, time: string, after: string) => ({
		text: `${before}${time}${after}`,
		parts: [{ text: before }, { text: time, time: true }, { text: after }],
	});

	if (!plan) {
		return verb("NO TRAIN", "Nothing usable in the lookahead window.", "bad");
	}

	const trainAt = clock(plan.boarding.predictedMs, timezone);
	const minutesToDeparture = Math.max(0, Math.round((plan.boarding.predictedMs - nowMs) / 60_000));

	if (minutesToDeparture > FAR_FUTURE_MINUTES) {
		// Another day: report the plan, not the urgency, which would be computed against a
		// "now" the rider is not living in.
		const departure = clockParts(hasTravelLeg ? (plan.leaveByMs ?? plan.boarding.predictedMs) : plan.boarding.predictedMs, timezone);
		const day = new Date(plan.boarding.predictedMs).toLocaleDateString("en-US", { weekday: "long", timeZone: timezone });
		return {
			lead: hasTravelLeg ? "LEAVE BY" : "TRAIN",
			hero: departure.time,
			heroMeridiem: departure.meridiem,
			unit: null,
			sub: `${day} · scheduled`,
			tone: "info",
		};
	}

	// Driving and standing on the platform get the card to themselves, ahead of the verbs
	// below. Those verbs are all instructions about leaving the house, which is settled by the
	// time either of these applies, and "GO NOW" to someone already halfway there is nonsense.
	// The status stays a number in both; the tag and the colour carry what has gone wrong.
	if (mode === "platform") {
		const minutes = Math.round((plan.boarding.predictedMs - nowMs) / 60_000);
		return {
			// Counting *in* to a train that has gone is a lie the number then contradicts. The row's
			// tag says MISSED off the same `departed`, so the two cannot disagree.
			lead: plan.urgency === "departed" ? "TRAIN MISSED" : "TRAIN IN",
			hero: `${minutes}`,
			heroMeridiem: null,
			unit: minuteWord(Math.abs(minutes)),
			sub: `${trainAt} to ${headsignLabel(plan.headsign)}`,
			tone: toneForWait(minutes),
		};
	}

	/*
	 * He is on the train, so nothing about catching it can still be true. None of the verbs
	 * below is allowed to run: being warned about a train he is sitting on was the complaint
	 * that put this card here. The only number left worth having is when he gets off — and
	 * which stop that is, because the ride home has two and he chooses on the way.
	 */
	if (mode === "onboard") {
		const arrival = plan.destinations[destinationIndex] ?? plan.destinations[0] ?? null;
		if (!arrival) {
			return verb("ON BOARD", `The ${trainAt} to ${headsignLabel(plan.headsign)}.`, "good");
		}
		const stop = stationLabel(arrival.stopName);
		// The deck keeps this train for a few minutes after it lands, because stepping off takes
		// some of them. Counting down to a time already gone would just read zero.
		if (arrival.predictedMs <= nowMs) {
			const landed = says(`${stop} at `, clock(arrival.predictedMs, timezone), ".");
			return verb("ARRIVED", landed.text, "good", undefined, landed.parts);
		}
		const minutes = Math.max(0, Math.round((arrival.predictedMs - nowMs) / 60_000));
		// The platform time and the walk after it are both in the facts underneath, so saying them
		// here was the same two numbers twice. Where the train is next is the one thing the facts
		// cannot show, and it comes from this train's own timetable rather than the followed one,
		// so every card can answer it.
		const ahead = nextStopAhead(plan, nowMs);
		const approaching = ahead && ahead.stopId !== arrival.stopId ? ahead : null;
		// How many times the doors still open before his, counting his own. Left off when his is
		// next, where "1 more stop" says less than the station name already beside it.
		const remaining = stopsRemaining(plan, arrival.stopId, nowMs);
		return {
			lead: `ARRIVING ${stop.toUpperCase()} IN`,
			hero: `${minutes}`,
			heroMeridiem: null,
			unit: minuteWord(minutes),
			sub: approaching
				? `${approaching.predictedMs - nowMs <= ARRIVING_SECONDS * 1000 ? "arriving" : "next"} ${stationLabel(approaching.stopName)}`
				: "",
			note:
				remaining > 1
					? { value: `${remaining}`, label: "more stops", tone: "good" }
					: remaining === 1
						? { value: "NEXT", label: "STOP", tone: "good" }
						: undefined,
			tone: "good",
		};
	}

	// The station buffer stays out of this: it is a planning allowance for the walk and the
	// faregates, and subtracting it here would report a margin he cannot see on any clock.
	if (mode === "drive" && plan.stationArrivalMs != null) {
		const spare = Math.round((plan.boarding.predictedMs - plan.stationArrivalMs) / 60_000);
		const dropOff = clock(plan.stationArrivalMs, timezone);
		// The buffer is what the plan assumed the walk and the faregates would take, so arriving
		// with at least that much is the definition of on track.
		const buffer = Math.round(plan.stationBufferSeconds / 60);
		return {
			lead: "TIME TO SPARE",
			hero: `${spare}`,
			heroMeridiem: null,
			unit: minuteWord(Math.abs(spare)),
			sub: `drop off ${dropOff} · train ${trainAt}`,
			subParts: [
				{ text: "drop off " },
				{ text: dropOff, time: true },
				{ text: " · train " },
				{ text: trainAt, time: true },
			],
			tone: spare >= buffer ? "good" : spare >= Math.max(2, Math.round(buffer / 2)) ? "warn" : "bad",
		};
	}

	if (plan.urgency === "departed") {
		const gone = says("The ", trainAt, " has left. Swipe for the next one.");
		return verb("MISSED", gone.text, "bad", undefined, gone.parts);
	}

	// The margin the whole plan rests on. Until the leave moment arrives it is the margin the plan
	// *promises* — what he will have if he goes at the leave cue, including both buffers.
	// Counting the live one from an hour out answered a question nobody asked:
	// "LEAVE IN 40 minutes" beside "47 minutes to spare" is two numbers, and the larger one only
	// applies to a departure he is not making. Once he is at or past the leave moment the promise
	// is spent and the live figure is the one that matters, and the two meet without a jump.
	const leaveMomentMs = plan.startByMs ?? plan.leaveByMs;
	const beforeLeaving = plan.feasible && leaveMomentMs != null && leaveMomentMs - nowMs >= 30_000;
	const spare = hasTravelLeg ? (beforeLeaving ? Math.round((plan.stationBufferSeconds + plan.prepSeconds) / 60) : spareMinutes(plan)) : null;
	const note =
		spare == null
			? undefined
			: spare < 0
				? { value: `${-spare}`, label: `${minuteWord(-spare)} short`, tone: "bad" as Tone }
				: {
						value: `${spare}`,
						label: `${minuteWord(spare)} ${beforeLeaving ? "to spare at departure" : "to spare"}`,
						tone: spareTone(spare, plan),
					};

	if (!plan.feasible) {
		// It has not left, but it will be gone before the rider can get there.
		const missing = says("The ", trainAt, " will have left.");
		return verb("WILL MISS", missing.text, "bad", note, missing.parts);
	}
	/*
	 * Urgency is measured on slack, which is time until departure less the drive.
	 *
	 * With a drive these read as the same card as LEAVE NOW, because they are the same instruction
	 * with the volume up — and the minutes they used to spell out in prose are the spare line now,
	 * where they are a number rather than a sentence. Without a drive there is nothing to leave for,
	 * so the words stay about the train.
	 */
	if (plan.urgency === "run") {
		const line = hasTravelLeg ? says("For the ", trainAt, " train.") : says("The ", trainAt, " train leaves in about a minute.");
		return verb("RUN", line.text, "bad", hasTravelLeg ? note : undefined, line.parts);
	}
	if (plan.urgency === "hurry") {
		const line = hasTravelLeg ? says("For the ", trainAt, " train.") : says("The ", trainAt, " train leaves in about two minutes.");
		return verb(hasTravelLeg ? "GO NOW" : "HURRY", line.text, "bad", hasTravelLeg ? note : undefined, line.parts);
	}

	if (!hasTravelLeg) {
		return {
			lead: "TRAIN IN",
			hero: `${minutesToDeparture}`,
			heroMeridiem: null,
			unit: minuteWord(minutesToDeparture),
			sub: `${trainAt} to ${headsignLabel(plan.headsign)}`,
			tone: toneForWait(minutesToDeparture),
		};
	}

	if (plan.leaveByMs == null) {
		return { lead: "LEAVE BY", hero: "--:--", heroMeridiem: null, unit: null, sub: "No drive estimate available.", tone: "info" };
	}

	/*
	 * One number, and it counts to the moment he has to start moving.
	 *
	 * The out-the-door buffer is part of leaving — bathroom, keys, the walk to the car — so a
	 * countdown that ignored it was describing a departure nobody makes. It used to appear only as
	 * the early end of a range, which read as two answers to one question and invited taking the
	 * wrong one. Now it is in every quote of the leave time, including the clock one further out.
	 */
	const startMs = plan.startByMs ?? plan.leaveByMs;
	const secondsUntilStart = (startMs - nowMs) / 1000;
	const leaveAt = clock(startMs, timezone);
	// Rounded first: "leave in 0 minutes" is what half a minute looks like after rounding, and it
	// is a worse way of saying the thing there is already a word for.
	const minutes = Math.round(secondsUntilStart / 60);

	if (minutes <= 0) {
		// Past the comfortable leave time but the drive still fits, so this is urgent, not lost.
		const now = says("For the ", trainAt, " train.");
		return verb("LEAVE NOW", now.text, "warn", note, now.parts);
	}

	// Past an hour a countdown is arithmetic the rider has to do twice: once to read it, once to
	// place it in the morning. A clock time is the thing they were going to work out anyway.
	if (minutes > COUNTDOWN_WITHIN_MINUTES) {
		const departure = clockParts(startMs, timezone);
		return {
			lead: "LEAVE BY",
			hero: departure.time,
			heroMeridiem: departure.meridiem,
			unit: null,
			sub: `for the ${trainAt} train`,
			subParts: [{ text: "for the " }, { text: trainAt, time: true }, { text: " train" }],
			tone: toneForUrgency(plan.urgency),
		};
	}

	return {
		lead: "LEAVE IN",
		hero: `${minutes}`,
		heroMeridiem: null,
		unit: minuteWord(minutes),
		sub: `By ${leaveAt} · for the ${trainAt} train`,
		subParts: [{ text: "By " }, { text: leaveAt, time: true }, { text: " · for the " }, { text: trainAt, time: true }, { text: " train" }],
		note: temper(note, toneForUrgency(plan.urgency)),
		tone: toneForUrgency(plan.urgency),
	};
}

/** Worst first, so two verdicts on the same card can be compared. */
const SEVERITY: Tone[] = ["bad", "warn", "info", "neutral", "good"];

/**
 * Keep the margin from reading better than the card it sits on.
 *
 * At the leave-by moment the spare equals the station buffer by construction, so it graded itself
 * green under an amber LEAVE NOW — one card saying two things at once. It may still be worse than
 * the card, which is an escalation rather than a contradiction.
 */
function temper(note: CardStatus["note"], cardTone: Tone): CardStatus["note"] {
	if (!note || SEVERITY.indexOf(note.tone) <= SEVERITY.indexOf(cardTone)) {
		return note;
	}
	return { ...note, tone: cardTone };
}

/**
 * How the margin is doing, graded against what the plan assumed the walk and faregates take.
 *
 * The same test the driving card uses for its own spare, so the two never disagree about whether
 * the same number of minutes is comfortable.
 */
function spareTone(spare: number, plan: CommutePlan): Tone {
	const buffer = Math.round(plan.stationBufferSeconds / 60);
	if (spare >= buffer) {
		return "good";
	}
	return spare >= Math.max(2, Math.round(buffer / 2)) ? "warn" : "bad";
}

/**
 * Minutes he would have at the station before his train goes, if he left this second.
 *
 * The station buffer stays out of it deliberately: that is a planning allowance for the walk and
 * the faregates, and subtracting it would report a margin no clock agrees with. Negative means
 * the train is gone before he can get there.
 */
function spareMinutes(plan: CommutePlan): number | null {
	return plan.stationArrivalMs == null ? null : Math.round((plan.boarding.predictedMs - plan.stationArrivalMs) / 60_000);
}

/** Beyond this the deadline is given as a clock time rather than counted down to. */
const COUNTDOWN_WITHIN_MINUTES = 60;

/** Spelled out on the card, where there is room; everything else stays abbreviated. */
function minuteWord(value: number): string {
	return value === 1 ? "minute" : "minutes";
}

function toneForUrgency(urgency: Urgency): Tone {
	switch (urgency) {
		case "relax":
			return "good";
		case "soon":
			return "info";
		case "tight":
			return "warn";
		default:
			return "bad";
	}
}

/**
 * Colour on a train countdown follows the countdown and nothing else.
 *
 * Urgency is measured on slack, which counts a drive that is already behind him. Left to it, a
 * train thirteen minutes from a platform he is standing on came out red.
 */
function toneForWait(minutes: number): Tone {
	if (minutes <= 0) {
		return "bad";
	}
	if (minutes <= 2) {
		return "warn";
	}
	return minutes <= 5 ? "info" : "good";
}

export function statusLabel(status: PlanStatus): string {
	switch (status) {
		case "ideal":
			return "ON TARGET";
		case "early":
			return "EARLY";
		case "tight":
			return "TIGHT";
		case "late":
			return "LATE";
		case "missed":
			return "MISSED";
	}
}

export function urgencyTone(urgency: Urgency): Tone {
	return toneForUrgency(urgency);
}

export function statusTone(status: PlanStatus): "good" | "warn" | "bad" | "info" {
	switch (status) {
		case "ideal":
			return "good";
		case "early":
			return "info";
		case "tight":
			return "warn";
		case "late":
		case "missed":
			return "bad";
	}
}

export function healthTone(level: ServiceHealth): "good" | "warn" | "bad" | "info" {
	switch (level) {
		case "normal":
			return "good";
		case "minor":
			return "warn";
		case "major":
			return "bad";
		case "unknown":
			return "info";
	}
}

/** Time until the rider must be moving. Replaces platform wait, which said the same thing twice. */
export function slackLabel(plan: CommutePlan): string {
	if (plan.urgency === "departed") {
		return "left";
	}
	if (!plan.feasible) {
		return "too far";
	}
	if (plan.slackSeconds == null) {
		return "--";
	}
	if (plan.slackSeconds <= 60) {
		return "run";
	}
	return `${Math.round(plan.slackSeconds / 60)} min`;
}

/**
 * Tag for a train on another day.
 *
 * Urgency is meaningless across a 13 hour gap, so this reports only the relationship to the
 * target window — except that several trains usually land inside it, and a row of identical
 * pills does not say which one the alert will actually name. That one is told to be taken; the
 * rest stay green, because they get him there on time too.
 */
export function previewTag(plan: CommutePlan, recommendedId?: string | null): Tag {
	switch (plan.status) {
		case "late":
			return { text: "LATE", tone: "bad" };
		case "early":
			return { text: "EARLY", tone: "info" };
		case "tight":
			return { text: "TIGHT", tone: "warn" };
		default:
			if (recommendedId != null && plan.id !== recommendedId) {
				return { text: "ALSO WORKS", tone: "good" };
			}
			return { text: "TAKE THIS", tone: "good" };
	}
}

export interface Tag {
	text: string;
	tone: Tone;
}

/**
 * The train that gets the green tag.
 *
 * Normally the recommendation, but if catching that one means running, the green goes to the
 * next comfortable train instead. Green should mean "this is the one to relax into", so a
 * board where the only green option is a sprint is telling the rider the wrong thing.
 */
export function greenPlanId(snapshot: CommuteSnapshot): string | null {
	const recommended = snapshot.options.find((option) => option.id === snapshot.recommendedId);
	if (recommended && recommended.feasible && recommended.status !== "tight") {
		return recommended.id;
	}

	const after = snapshot.options.filter(
		(option) => option.feasible && (!recommended || option.boarding.predictedMs > recommended.boarding.predictedMs),
	);
	const comfortable = after.find((option) => option.status === "ideal") ?? after.find((option) => option.status !== "late");
	return comfortable?.id ?? recommended?.id ?? null;
}

/**
 * The tag on a train row.
 *
 * Colour is a verdict on the arrival, not a ranking of the board: anything that lands inside the
 * window is green, because it gets him there on time and that is the only question the colour
 * answers. One of them is additionally named as the pick. Blue is reserved for arriving before
 * the window opens, yellow for scraping its far edge, red for missing it.
 *
 * Standing on the platform changes what the words can honestly mean. "Tight" and "will miss"
 * are both verdicts on getting there, and getting there is done; what is left to say about a
 * train a few minutes out is that it is nearly here.
 */
export function planTag(plan: CommutePlan, snapshot: CommuteSnapshot, mode: CardMode = "leave", nowMs = Date.now()): Tag {
	const arrived = mode === "platform" || mode === "onboard";
	/*
	 * On board, the whole route is on offer so he can name the train he is actually on, and none
	 * of it can be missed — he is sitting on one of them. With a deadline still to meet the
	 * arrival is worth grading; without one the useful thing to say is where each train has got
	 * to, which is what tells him which is his.
	 */
	if (mode === "onboard") {
		if (snapshot.targetWindow && plan.finalArrivalMs != null) {
			return arrivalTag(plan, snapshot);
		}
		const behind = lastStopPassed(plan, nowMs);
		return behind ? { text: stationLabel(behind.stopName).toUpperCase(), tone: "info" } : { text: "NOT YET OUT", tone: "info" };
	}

	if (plan.urgency === "departed") {
		return { text: "MISSED", tone: "bad" };
	}
	if (!plan.feasible && !arrived) {
		return { text: "WILL MISS", tone: "bad" };
	}
	if (plan.status === "late") {
		return { text: "LATE", tone: "bad" };
	}
	if (plan.status === "early") {
		return { text: "EARLY", tone: "info" };
	}

	// Checked before the pick, or a board with nothing comfortable on it would paint its least
	// bad option green and hide the warning that is the whole point of the colour.
	if (plan.status === "tight") {
		if (arrived && (plan.urgency === "run" || plan.urgency === "hurry")) {
			return { text: "ARRIVING", tone: "good" };
		}
		if (arrived && plan.urgency === "tight") {
			return { text: "SOON", tone: "good" };
		}
		return { text: "TIGHT", tone: "warn" };
	}

	return arrivalTag(plan, snapshot);
}

/** The verdict on where this train lands him, with no opinion about catching it. */
function arrivalTag(plan: CommutePlan, snapshot: CommuteSnapshot): Tag {
	if (plan.status === "late") {
		return { text: "LATE", tone: "bad" };
	}
	if (plan.status === "early") {
		return { text: "EARLY", tone: "info" };
	}
	if (plan.status === "tight") {
		return { text: "TIGHT", tone: "warn" };
	}
	if (plan.id === greenPlanId(snapshot)) {
		return { text: snapshot.targetWindow ? "TAKE THIS" : "NEXT", tone: "good" };
	}
	// Without a window there is nothing to be on time for, so these are alternatives rather than
	// good outcomes, and saying otherwise would be a colour with no fact behind it.
	return snapshot.targetWindow ? { text: "IN WINDOW", tone: "good" } : { text: "OPTION", tone: "info" };
}

// The worker writes notifications read on the same phone as the cards, so the naming rules live
// where both can reach them. Re-exported here because this is where the app looks for wording.
export { headsignLabel, stationLabel } from "../shared/labels";
